import {
  GridColDef,
  GridSortModel,
  GridValidRowModel,
} from '@mui/x-data-grid-pro'
import {
  getFontCss,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import ActiveCardTileSvg from 'src/assets/images/graphic-cardTile-Active.png'
import PendingCardTileSvg from 'src/assets/images/graphic-cardTile-Pending.png'
import { chevronColumnDef } from 'src/components/DataGrid/cells/ChevronCell'
import DataGridCell from 'src/components/DataGrid/cells/DataGridCell/DataGridCell'
import DataGridCellWrapper from 'src/components/DataGrid/cells/DataGridCellWrapper/DataGridCellWrapper'
import DataGrid from 'src/components/DataGrid/DataGrid'
import ListItemToolTip from 'src/components/ListItemTooltip/ListItemTooltip'
import RecurrenceAmountAndFrequencyLabel from 'src/components/RecurrenceAmountAndFrequencyLabel/RecurrenceAmountAndFrequencyLabel'
import { translateSingleString, useTranslate } from 'src/localization/Trns'
import { ApprovalRuleResponse } from 'src/models/organizations.models'
import { RecurrenceModel } from 'src/models/recurrence.models'
import {
  ApprovalStatus,
  VirtualCardListModel,
} from 'src/models/virtualCard.models'
import { connectedPush } from 'src/navigation/Lnk'
import { updateVirtualCardUrl } from 'src/navigation/urls'
import {
  selectNullSafeLocaleFromState,
  selectUserIdFromState,
} from 'src/redux/selectors/auth.selectors'
import { selectUserListModelsFromState } from 'src/redux/selectors/data/users.selectors'
import styled, { css, CSSProp } from 'styled-components'

type PendingRequestsDataGridProps = {
  virtualCards: VirtualCardListModel[]
  cardApprovalRuleEnabled?: boolean
  cardApprovalRules?: ApprovalRuleResponse
  clientHeight: number
}

interface PendingRequestsDataGridRow extends GridValidRowModel {
  id: string
  virtualCard: VirtualCardListModel
  index: number
}

const StyledDiv = styled.div<{
  virtualCard?: VirtualCardListModel
  wrapText?: boolean
}>`
  ${getFontCss(
    14,
    themeConstants.colors.font_primary,
    styleConstants.fontFamilies.regular,
  )};
  overflow: hidden;
  ${({ wrapText }): CSSProp => {
    return wrapText
      ? css`
          white-space: nowrap;
        `
      : css``
  }}
  text-overflow: ellipsis;
  ${({ virtualCard }): CSSProp => {
    return virtualCard && virtualCard?.isPending
      ? css`
          ${getFontCss(
            14,
            styleConstants.colors.gray_dark,
            styleConstants.fontFamilies.regular,
          )};
        `
      : css``
  }}
`

const RequestedItem = styled.span`
  ${getFontCss(
    14,
    styleConstants.colors.gray_darkest,
    styleConstants.fontFamilies.regular,
  )};
`

const RequestedValue = styled.span`
  ${getFontCss(
    14,
    styleConstants.colors.gray_darkest,
    styleConstants.fontFamilies.regular,
  )};
`

const PendingRequestsDataGrid: React.FC<PendingRequestsDataGridProps> = (
  props,
): React.ReactElement => {
  const translate = useTranslate()

  const currentUserId = useSelector(selectUserIdFromState)
  const users = useSelector(selectUserListModelsFromState)
  const locale = useSelector(selectNullSafeLocaleFromState)
  const { analytics } = useAnalyticsContext()

  const [sortModel, setSortModel] = React.useState<GridSortModel>()

  const isOrganizationCardApproverMe =
    props.cardApprovalRules?.approverId === currentUserId

  const dispatch = useDispatch()

  const rows: PendingRequestsDataGridRow[] = props.virtualCards.map(
    (virtualCard, i) => {
      return {
        id: virtualCard?.id,
        virtualCard: virtualCard,
        index: i,
      }
    },
  )

  const columns: Array<GridColDef<PendingRequestsDataGridRow>> = [
    {
      field: 'date',
      headerName: translateSingleString(locale, 'Date'),
      disableColumnMenu: true,
      sortable: false,
      minWidth: 150,
      renderCell: (params): React.ReactElement => {
        const vc = params.row.virtualCard
        return (
          <DataGridCell
            text={vc.updatedAtTimezonedMoment.format('L')}
            params={params}
          />
        )
      },
    },
    {
      field: 'cardName',
      headerName: translateSingleString(locale, 'Card Name'),
      disableColumnMenu: true,
      sortable: false,
      flex: 1,
      minWidth: 150,
      renderCell: (params): React.ReactElement => {
        const vc = params.row.virtualCard
        const showPendingFinalApprovalChip =
          props.cardApprovalRuleEnabled &&
          !isOrganizationCardApproverMe &&
          vc.awaitingApprovalStatus ===
            ApprovalStatus.AwaitingApproverRuleApproval
        const showRequiresFinalApprovalChip =
          props.cardApprovalRuleEnabled &&
          isOrganizationCardApproverMe &&
          vc.awaitingApprovalStatus ===
            ApprovalStatus.AwaitingApproverRuleApproval
        // TODO: figure out how this works in new design system
        return (
          <DataGridCellWrapper params={params}>
            <StyledDiv
              css={css`
                ${getFontCss(
                  14,
                  themeConstants.colors.font_primary,
                  styleConstants.fontFamilies.regular,
                )};
                ${(vc.isPending || vc.isInactive) &&
                getFontCss(
                  14,
                  styleConstants.colors.gray_dark,
                  styleConstants.fontFamilies.regular,
                )};
              `}
            >
              <div
                css={css`
                  display: flex;
                  align-items: center;
                `}
              >
                <img
                  css={css`
                    width: 45px;
                  `}
                  src={vc.isPending ? PendingCardTileSvg : ActiveCardTileSvg}
                  alt={translateSingleString(
                    locale,
                    vc.isPending ? 'Pending Card' : 'Active Card',
                  )}
                />
                <div
                  css={css`
                    margin-left: 20px;
                    display: flex;
                    flex-direction: column;
                  `}
                >
                  <div
                    css={css`
                      display: flex;
                      flex-wrap: wrap;
                    `}
                  >
                    <span
                      css={css`
                        ${getFontCss(
                          14,
                          themeConstants.colors.font_primary,
                          styleConstants.fontFamilies.regular,
                        )};
                      `}
                    >
                      {vc.isPending && translate('New Card')}
                      {!vc.isPending &&
                        translate('Update to ${cardNameWithLast4}', {
                          cardNameWithLast4: vc.displayName,
                        })}
                    </span>
                    <>
                      {!vc.isPending && (
                        <span
                          css={css`
                            margin-left: 6px;
                            ${getFontCss(
                              12,
                              styleConstants.colors.gray_darkest,
                              styleConstants.fontFamilies.regular,
                            )};
                          `}
                        >
                          {`(${vc.last4})`}
                        </span>
                      )}
                      {showPendingFinalApprovalChip && (
                        <span
                          css={css`
                            margin-left: 6px;
                          `}
                        >
                          <ListItemToolTip
                            tabText={translate('Pending Final Approval')}
                            tooltipText={translate(
                              'This card requires final approval because it is above the organization threshold amount',
                            )}
                          />
                        </span>
                      )}
                      {showRequiresFinalApprovalChip && (
                        <span
                          css={css`
                            margin-left: 6px;
                          `}
                        >
                          <ListItemToolTip
                            tabText={translate('Requires Final Approval')}
                            tooltipText={translate(
                              'This card requires your final approval',
                            )}
                          />
                        </span>
                      )}
                    </>
                  </div>
                  <span
                    css={css`
                      ${getFontCss(
                        12,
                        styleConstants.colors.gray_darkest,
                        styleConstants.fontFamilies.regular,
                      )};
                      white-space: nowrap;
                      overflow: hidden;
                      text-overflow: ellipsis;
                    `}
                  >
                    {users[vc.recipientId].truncatedFullNameOrEmail}
                  </span>
                </div>
              </div>
            </StyledDiv>
          </DataGridCellWrapper>
        )
      },
    },
    {
      field: 'details',
      headerName: translateSingleString(locale, 'Details'),
      disableColumnMenu: true,
      sortable: false,
      flex: 1,
      minWidth: 150,
      renderCell: (params): React.ReactElement => {
        const vc = params.row.virtualCard
        return (
          <DataGridCellWrapper params={params}>
            <StyledDiv
              wrapText
              css={css`
                ${vc.isInactive &&
                getFontCss(
                  14,
                  styleConstants.colors.gray_dark,
                  styleConstants.fontFamilies.regular,
                )};
              `}
            >
              <div
                css={css`
                  display: flex;
                  flex-direction: column;
                `}
              >
                {(vc.pending?.balanceLabel(vc.currency) !== undefined ||
                  vc.isPending) && (
                  <RequestedItem>
                    {vc.isPending &&
                      translate('Credit Limit: ${amount}', {
                        amount: (
                          <RequestedValue>
                            {vc.pending?.balanceLabel(vc.currency) ||
                              vc.balanceLabel}
                          </RequestedValue>
                        ),
                      })}
                    {!vc.isPending &&
                      translate('Available Credit: ${amount}', {
                        amount: (
                          <RequestedValue>
                            {vc.pending?.balanceLabel(vc.currency) ||
                              vc.balanceLabel}
                          </RequestedValue>
                        ),
                      })}
                  </RequestedItem>
                )}
                {/* TODO: revert back to activeUntil */}
                {(vc.pending?.validToTimezonedString(vc.timezone) !==
                  undefined ||
                  vc.isPending) && (
                  <RequestedItem>
                    {translate('Active Until: ${activeUntil}', {
                      activeUntil: (
                        <RequestedValue>
                          {vc.isPending && vc.recurs
                            ? translate('See Request Details')
                            : vc.pending?.validToTimezonedString(vc.timezone) ||
                              vc.validToTimezonedString}
                        </RequestedValue>
                      ),
                    })}
                  </RequestedItem>
                )}
                {(vc.pending?.recurs === true ||
                  vc.pending?.recurs === false) && (
                  <RequestedItem>
                    {translate('Card Type: ${cardType}', {
                      cardType: (
                        <RequestedValue>
                          {vc.pending.recurs === true
                            ? translate('Recurring')
                            : translate('Standard')}
                        </RequestedValue>
                      ),
                    })}
                  </RequestedItem>
                )}
                {(vc.pending?.recurrence !== undefined ||
                  (vc.isPending && vc.recurrence !== undefined)) && (
                  <RequestedItem>
                    {translate('Recurring: ${recurring}', {
                      recurring: (
                        <RequestedValue>
                          <RecurrenceAmountAndFrequencyLabel
                            period={
                              (
                                vc.pending?.recurrence ||
                                (vc.recurrence as RecurrenceModel)
                              ).period
                            }
                            balance={
                              (
                                vc.pending?.recurrence ||
                                (vc.recurrence as RecurrenceModel)
                              ).balance
                            }
                            interval={
                              (
                                vc.pending?.recurrence ||
                                (vc.recurrence as RecurrenceModel)
                              ).interval
                            }
                            byWeekDay={
                              (
                                vc.pending?.recurrence ||
                                (vc.recurrence as RecurrenceModel)
                              ).byWeekDay
                            }
                            byMonthDay={
                              (
                                vc.pending?.recurrence ||
                                (vc.recurrence as RecurrenceModel)
                              ).byMonthDay
                            }
                            byYearDay={
                              (
                                vc.pending?.recurrence ||
                                (vc.recurrence as RecurrenceModel)
                              ).byYearDay
                            }
                            timezone={vc.timezone}
                            currency={vc.currency}
                          />
                        </RequestedValue>
                      ),
                    })}
                  </RequestedItem>
                )}
              </div>
            </StyledDiv>
          </DataGridCellWrapper>
        )
      },
    },
    chevronColumnDef,
  ]

  return (
    <DataGrid
      height={props.clientHeight}
      rows={rows}
      columns={columns}
      rowHeight={75}
      infiniteScrollProps={{
        fetch: (): void => undefined,
        loading: false,
        complete: false,
      }}
      filtersBody={{}}
      sortModel={sortModel}
      onSortModelChange={(newSortModel): void => setSortModel(newSortModel)}
      onRowClick={(row): void => {
        const virtualCard = row.row.virtualCard
        dispatch(
          connectedPush({
            pathname: updateVirtualCardUrl(virtualCard?.id),
            search: '',
            hash: '',
            state: {},
          }),
        )
        analytics?.track('virtual cards - select virtual card - item press', {
          intent: 'select virtual card',
          virtualCardSelected: virtualCard.id,
          interaction: 'item press',
        })
      }}
    />
  )
}

export default PendingRequestsDataGrid
